'use client';

import React, { useState } from 'react';
import { Plus, Search, Receipt, Edit2, Trash2, Share2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import ExpenseEntryForm from './ExpenseEntryForm';
import ExpenseCategoryChart from './ExpenseCategoryChart';

const EXPENSE_CATEGORIES = [
  'Hamali (Loading)',
  'Hamali (Unloading)',
  'Labour Charges',
  'Transport / Lorry',
  'Market Fee (Tolai)',
  'Weighbridge Charges',
  'Miscellaneous',
];

const EXPENSES_DATA = [
  {
    id: 'exp-001',
    date: '18/06/2026',
    lotNumber: '470',
    product: 'Potato',
    category: 'Hamali (Unloading)',
    vendor: 'Ramu Labour Group',
    amount: 3200,
    notes: '150 bags × ₹21.33/bag',
    receipt: true,
  },
  {
    id: 'exp-002',
    date: '18/06/2026',
    lotNumber: '470',
    product: 'Potato',
    category: 'Market Fee (Tolai)',
    vendor: 'KKNMM Market',
    amount: 2100,
    notes: 'Market fee 0.28% of declared value',
    receipt: true,
  },
  {
    id: 'exp-003',
    date: '17/06/2026',
    lotNumber: '300',
    product: 'Onion',
    category: 'Labour Charges',
    vendor: 'Senthil Labour',
    amount: 1800,
    notes: 'Grade sorting and weighing',
    receipt: false,
  },
  {
    id: 'exp-004',
    date: '17/06/2026',
    lotNumber: '300',
    product: 'Onion',
    category: 'Market Fee (Tolai)',
    vendor: 'KKNMM Market',
    amount: 3100,
    notes: 'Market fee for Lot #300',
    receipt: true,
  },
  {
    id: 'exp-005',
    date: '16/06/2026',
    lotNumber: '185',
    product: 'Onion',
    category: 'Transport / Lorry',
    vendor: 'Murugan Lorry Service',
    amount: 4800,
    notes: 'Chennai to Koyambedu last mile',
    receipt: true,
  },
  {
    id: 'exp-006',
    date: '16/06/2026',
    lotNumber: '185',
    product: 'Onion',
    category: 'Hamali (Loading)',
    vendor: 'Ramu Labour Group',
    amount: 3600,
    notes: '180 bags × ₹20/bag',
    receipt: false,
  },
  {
    id: 'exp-007',
    date: '15/06/2026',
    lotNumber: '211',
    product: 'Tomato',
    category: 'Weighbridge Charges',
    vendor: 'City Weighbridge',
    amount: 500,
    notes: 'Entry weighment',
    receipt: true,
  },
  {
    id: 'exp-008',
    date: '15/06/2026',
    lotNumber: '410',
    product: 'Garlic',
    category: 'Labour Charges',
    vendor: 'Selvam Labour',
    amount: 2200,
    notes: 'Grading and sorting — premium garlic',
    receipt: false,
  },
  {
    id: 'exp-009',
    date: '14/06/2026',
    lotNumber: '211',
    product: 'Tomato',
    category: 'Market Fee (Tolai)',
    vendor: 'KKNMM Market',
    amount: 2800,
    notes: 'Market fee Lot #211',
    receipt: true,
  },
  {
    id: 'exp-010',
    date: '14/06/2026',
    lotNumber: '211',
    product: 'Tomato',
    category: 'Miscellaneous',
    vendor: 'Various',
    amount: 700,
    notes: 'Twine, tags, bags for repacking',
    receipt: false,
  },
];

const CATEGORY_COLORS: Record<string, string> = {
  'Hamali (Loading)': 'bg-primary/10 text-primary border-primary/20',
  'Hamali (Unloading)': 'bg-primary/10 text-primary border-primary/20',
  'Labour Charges': 'bg-info/10 text-info border-info/20',
  'Transport / Lorry': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'Market Fee (Tolai)': 'bg-warning/10 text-warning border-warning/20',
  'Weighbridge Charges': 'bg-accent/10 text-accent border-accent/20',
  'Miscellaneous': 'bg-muted text-muted-foreground border-border',
};

export default function ExpenseManagementClient() {
  const [search, setSearch] = useState('');
  const [lotFilter, setLotFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);

  const uniqueLots = Array.from(new Set(EXPENSES_DATA.map((e) => e.lotNumber)));

  const filtered = EXPENSES_DATA.filter((exp) => {
    const matchSearch =
      exp.lotNumber.includes(search) ||
      exp.category.toLowerCase().includes(search.toLowerCase()) ||
      exp.vendor.toLowerCase().includes(search.toLowerCase()) ||
      exp.product.toLowerCase().includes(search.toLowerCase());
    const matchLot = lotFilter === 'All' || exp.lotNumber === lotFilter;
    const matchCat = categoryFilter === 'All' || exp.category === categoryFilter;
    return matchSearch && matchLot && matchCat;
  });

  const totalExpenses = filtered.reduce((s, e) => s + e.amount, 0);

  // Lot-wise totals
  const lotTotals = uniqueLots.map((lot) => ({
    lot,
    total: EXPENSES_DATA.filter((e) => e.lotNumber === lot).reduce((s, e) => s + e.amount, 0),
    product: EXPENSES_DATA.find((e) => e.lotNumber === lot)?.product || '',
  }));

  const handleWhatsAppShare = () => {
    const lotLabel = lotFilter !== 'All' ? `Lot #${lotFilter}` : 'All Lots';
    const catLabel = categoryFilter !== 'All' ? categoryFilter : 'All Categories';

    let message = `*Expense Report — ${lotLabel}*\n`;
    message += `Filter: ${catLabel}\n`;
    message += `Total Expenses: ₹${totalExpenses.toLocaleString('en-IN')}\n\n`;

    const grouped: Record<string, number> = {};
    filtered.forEach((e) => {
      grouped[e.category] = (grouped[e.category] || 0) + e.amount;
    });

    message += `*Category Breakdown:*\n`;
    Object.entries(grouped).forEach(([cat, amt]) => {
      message += `• ${cat}: ₹${amt.toLocaleString('en-IN')}\n`;
    });

    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Top Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by lot, category, vendor..."
            className="input-field pl-9"
          />
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary text-sm py-2" onClick={handleWhatsAppShare}>
            <Share2 size={14} />
            WhatsApp
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary text-sm py-2"
          >
            <Plus size={14} />
            Add Expense
          </button>
        </div>
      </div>

      {/* Summary + Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Lot-wise Summary */}
        <div className="card-base p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Lot-wise Expenses</h3>
          <div className="space-y-2">
            {lotTotals.map((lt) => (
              <div
                key={`lot-total-${lt.lot}`}
                onClick={() => setLotFilter(lotFilter === lt.lot ? 'All' : lt.lot)}
                className={`flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-colors ${
                  lotFilter === lt.lot ? 'bg-primary/10 border border-primary/20' : 'bg-muted/30 hover:bg-muted/50'
                }`}
              >
                <div>
                  <span className="lot-number text-xs font-bold text-primary">#{lt.lot}</span>
                  <span className="text-xs text-muted-foreground ml-2">{lt.product}</span>
                </div>
                <span className="text-sm font-bold amount-display text-foreground">
                  ₹{lt.total.toLocaleString('en-IN')}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="text-xs font-semibold text-foreground">Total (filtered)</span>
              <span className="text-sm font-bold amount-display text-primary">₹{totalExpenses.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </div>

        {/* Category Chart */}
        <div className="lg:col-span-2">
          <ExpenseCategoryChart expenses={EXPENSES_DATA} />
        </div>
      </div>

      {/* Category Filter Chips */}
      <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
        {['All', ...EXPENSE_CATEGORIES].map((cat) => (
          <button
            key={`cat-filter-${cat}`}
            onClick={() => setCategoryFilter(cat)}
            className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg border transition-all ${
              categoryFilter === cat
                ? 'bg-primary/10 text-primary border-primary/30' :'bg-muted text-muted-foreground border-border hover:border-primary/30'
            }`}
          >
            {cat === 'All' ? 'All Categories' : cat}
          </button>
        ))}
      </div>

      {/* Expenses Table */}
      <div className="card-base overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-muted/30 border-b border-border">
              <tr>
                <th className="table-header-cell">Date</th>
                <th className="table-header-cell">Lot #</th>
                <th className="table-header-cell">Product</th>
                <th className="table-header-cell">Category</th>
                <th className="table-header-cell">Vendor</th>
                <th className="table-header-cell">Amount</th>
                <th className="table-header-cell">Notes</th>
                <th className="table-header-cell">Receipt</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((exp) => (
                <tr key={exp.id} className="table-row">
                  <td className="table-cell text-xs">{exp.date}</td>
                  <td className="table-cell">
                    <span className="lot-number font-bold text-primary">#{exp.lotNumber}</span>
                  </td>
                  <td className="table-cell text-sm">{exp.product}</td>
                  <td className="table-cell">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-2xs font-semibold border ${CATEGORY_COLORS[exp.category] || 'bg-muted text-muted-foreground border-border'}`}>
                      {exp.category}
                    </span>
                  </td>
                  <td className="table-cell text-sm">{exp.vendor}</td>
                  <td className="table-cell">
                    <span className="font-bold text-danger amount-display">₹{exp.amount.toLocaleString('en-IN')}</span>
                  </td>
                  <td className="table-cell">
                    <span className="text-xs text-muted-foreground truncate max-w-[160px] block">{exp.notes}</span>
                  </td>
                  <td className="table-cell">
                    {exp.receipt ? (
                      <span className="text-xs text-accent flex items-center gap-1">
                        <Receipt size={11} />
                        Yes
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="table-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button
                        className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title="Edit expense"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors"
                        title="Delete expense — this cannot be undone"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
              <Receipt size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm font-semibold text-foreground">No expenses found</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">
              No expenses match your current filters. Try adjusting the lot or category filter.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            {filtered.length} expenses · Total: <span className="text-danger font-bold amount-display">₹{totalExpenses.toLocaleString('en-IN')}</span>
          </p>
          <div className="flex items-center gap-1">
            {[1].map((page) => (
              <button
                key={`exp-page-${page}`}
                className="w-7 h-7 rounded-lg text-xs font-medium bg-primary/10 text-primary border border-primary/30"
              >
                {page}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Add Expense Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Expense"
        size="md"
      >
        <ExpenseEntryForm onClose={() => setShowAddModal(false)} />
      </Modal>
    </div>
  );
}